import {
  ActionIcon,
  Autocomplete,
  Box,
  Button,
  Divider,
  Group,
  Input,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconCalendarEvent } from '@tabler/icons-react';
import {
  addCalendarDays,
  parseTreatmentPlanDraft,
  resolveTreatmentPlanExpirationDays,
} from '@unify/fhir-model/resources/treatment-plan';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import {
  useQuestionnaireForm,
  type QuestionnaireFormLoadedState,
  type QuestionnaireFormPaginationState,
  type QuestionnaireFormSinglePageState,
  type QuestionnaireFormState,
} from '@medplum/react-hooks';
import { IconClipboardList, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { DiagnosisSelect } from '../../../components/DiagnosisSelect';
import { getDiagnosisDescription } from '../../../data/diagnosisCodes';
import {
  buildDiagnosisCodeAndDescription,
  filterResponseItemsByLinkId,
  findQuestionnaireItemByLinkIdSuffix,
  getAnswerString,
  getNestedResponseItem,
  isCoreTreatmentPlanTopLevelItem,
  removeRepeatableGroupInstance,
  splitDiagnosisCodeAndDescription,
} from '../../../lib/treatmentPlanFormHelpers';
import type { VariableContext } from '../../../lib/template-variables';
import { PreviewItemArray } from '../../settings/template-library/components/TemplatePreviewDrawer';
import { formatPacificDateTime, isoToPacificWallClock, pacificWallClockToIso } from '../../../lib/pacificTime';
import { defaultInputSize } from '../../../config/theme';
import {
  applyTreatmentPlanRevisionOperations,
  type TreatmentPlanRevisionSuggestion,
} from '../../../lib/treatmentPlanAiRevision';
import { shapeNestedResponseItemsForQuestionnaire } from '../../appointments/utils/questionnaireResponseShape';

const TREATMENT_PLAN_INPUT_BG = { backgroundColor: 'var(--mantine-color-white)' };
const TREATMENT_PLAN_TEXTAREA_MIN_ROWS = 3;

/** Common presets for the frequency Autocomplete — free text remains allowed. */
const TREATMENT_FREQUENCY_OPTIONS = [
  'Weekly',
  'Twice per week',
  'Every other week',
  'Monthly',
  'Every other month',
];

type LoadedQuestionnaireFormState = QuestionnaireFormSinglePageState | QuestionnaireFormPaginationState;

function isLoadedQuestionnaireFormState(
  formState: QuestionnaireFormState,
): formState is LoadedQuestionnaireFormState {
  return !('loading' in formState && formState.loading);
}

interface TreatmentPlanDocumentFormSectionProps {
  questionnaire: Questionnaire;
  savedResponse: QuestionnaireResponse | null;
  readOnlyLinkIds: Set<string>;
  isEditable: boolean;
  varCtx: VariableContext;
  headerContent?: ReactNode;
  onLiveResponseChange: (response: QuestionnaireResponse | undefined) => void;
  /** Fired on every actual form mutation (not renders) — drives autosave scheduling. */
  onFormChange?: () => void;
  pendingAiSuggestion?: TreatmentPlanRevisionSuggestion | null;
  onAiSuggestionApplied?: () => void;
  onAiSuggestionError?: (message: string) => void;
}

function setStringAnswer(
  formState: QuestionnaireFormLoadedState,
  context: QuestionnaireResponseItem[],
  item: QuestionnaireItem,
  value: string,
): void {
  formState.onChangeAnswer(context, item, [{ valueString: value }]);
}

function RepeatableTextField({
  value,
  onChange,
  onRemove,
  placeholder,
  minRows = TREATMENT_PLAN_TEXTAREA_MIN_ROWS,
  disabled,
  canRemove,
  label,
}: {
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onRemove?: () => void;
  placeholder: string;
  minRows?: number;
  disabled: boolean;
  canRemove?: boolean;
  label?: string;
}): JSX.Element {
  return (
    <Box>
      {label && (
        <Text size="xs" c="dimmed" fw={600} mb={4}>
          {label}
        </Text>
      )}
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <Textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          minRows={minRows}
          autosize
          style={{ flex: 1 }}
          readOnly={disabled}
          styles={{ input: TREATMENT_PLAN_INPUT_BG }}
        />
        {canRemove && !disabled && onRemove && (
          <ActionIcon
            variant="light"
            color="red"
            onClick={onRemove}
            aria-label="Remove"
            mt={4}
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </Group>
    </Box>
  );
}

function getPlanInfoDateAnswer(
  planInfoInstance: QuestionnaireResponseItem | undefined,
  item: QuestionnaireItem | undefined,
): string | undefined {
  if (!item) return undefined;
  return planInfoInstance?.item
    ?.find((child) => child.linkId === item.linkId)
    ?.answer?.[0]?.valueDateTime;
}

/** Read-only PT date/time display ('—' when absent). Uses Input.Wrapper at the
 *  same size as themed DateTimePicker labels (`defaultInputSize`). */
function TreatmentPlanDateText({ label, iso }: { label?: string; iso: string | undefined }): JSX.Element {
  return (
    <Input.Wrapper label={label} size={defaultInputSize}>
      <Text size={defaultInputSize} c="gray.8">{iso ? formatPacificDateTime(iso) : '—'}</Text>
    </Input.Wrapper>
  );
}

interface DiagnosisBlockProps {
  diagnosisInstance: QuestionnaireResponseItem;
  diagnosisIndex: number;
  diagnosisContext: QuestionnaireResponseItem[];
  formState: QuestionnaireFormLoadedState;
  diagnosisCodeItem: QuestionnaireItem;
  presentingProblemItem: QuestionnaireItem | undefined;
  behavioralDefinitionGroupItem: QuestionnaireItem;
  behavioralDefinitionTextItem: QuestionnaireItem;
  goalGroupItem: QuestionnaireItem;
  goalTextItem: QuestionnaireItem;
  objectiveGroupItem: QuestionnaireItem;
  objectiveTextItem: QuestionnaireItem;
  interventionsItem: QuestionnaireItem | undefined;
  fieldDisabled: boolean;
  canRemoveDiagnosis: boolean;
  onRemoveDiagnosis: () => void;
  onAddDiagnosis: () => void;
  refreshItem: QuestionnaireItem;
  refreshContext: QuestionnaireResponseItem[];
}

function DiagnosisBlock({
  diagnosisInstance,
  diagnosisIndex,
  diagnosisContext,
  formState,
  diagnosisCodeItem,
  presentingProblemItem,
  behavioralDefinitionGroupItem,
  behavioralDefinitionTextItem,
  goalGroupItem,
  goalTextItem,
  objectiveGroupItem,
  objectiveTextItem,
  interventionsItem,
  fieldDisabled,
  canRemoveDiagnosis,
  onRemoveDiagnosis,
  onAddDiagnosis,
  refreshItem,
  refreshContext,
}: DiagnosisBlockProps): JSX.Element {
  const diagnosisCode = getAnswerString(getNestedResponseItem(diagnosisInstance, '-diagnosis-code'));
  const { code: diagnosisCodeOnly, description: diagnosisDescription } = splitDiagnosisCodeAndDescription(diagnosisCode);

  const behavioralInstances = filterResponseItemsByLinkId(
    diagnosisInstance.item,
    behavioralDefinitionGroupItem.linkId,
  );
  const goalInstances = filterResponseItemsByLinkId(diagnosisInstance.item, goalGroupItem.linkId);

  const handleDiagnosisCodeChange = (code: string) => {
    if (fieldDisabled) return;
    const parsed = splitDiagnosisCodeAndDescription(code);
    const codePart = parsed.description ? parsed.code : code;
    const description = !codePart.trim()
      ? ''
      : parsed.description
        ? parsed.description
        : (getDiagnosisDescription(codePart.trim()) ?? diagnosisDescription);
    setStringAnswer(
      formState,
      diagnosisContext,
      diagnosisCodeItem,
      buildDiagnosisCodeAndDescription(codePart, description),
    );
  };

  const handleDiagnosisDescriptionChange = (description: string) => {
    if (fieldDisabled) return;
    const codePart = diagnosisCodeOnly || splitDiagnosisCodeAndDescription(diagnosisCode).code;
    if (!codePart.trim()) return;
    setStringAnswer(
      formState,
      diagnosisContext,
      diagnosisCodeItem,
      buildDiagnosisCodeAndDescription(codePart, description),
    );
  };

  const removeInstance = (
    parentContext: QuestionnaireResponseItem[],
    instance: QuestionnaireResponseItem,
  ) => {
    removeRepeatableGroupInstance(formState, parentContext, instance, {
      context: refreshContext,
      item: refreshItem,
    });
  };

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" align="center" mb="md">
        <Title order={5}>
          {diagnosisIndex === 0 ? 'Primary Diagnosis' : `Diagnosis ${diagnosisIndex + 1}`}
        </Title>
        {!fieldDisabled && (
          <Group gap="xs" wrap="nowrap">
            {diagnosisIndex === 0 && (
              <Button
                color="gold"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={onAddDiagnosis}
              >
                Add diagnosis
              </Button>
            )}
            {canRemoveDiagnosis && (
              <ActionIcon
                variant="light"
                color="red"
                onClick={onRemoveDiagnosis}
                aria-label={`Remove diagnosis ${diagnosisIndex + 1}`}
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        )}
      </Group>

      <Stack gap="md">
        <DiagnosisSelect
          code={diagnosisCodeOnly}
          description={diagnosisDescription}
          onCodeChange={handleDiagnosisCodeChange}
          onDescriptionChange={handleDiagnosisDescriptionChange}
          disabled={fieldDisabled}
        />

        {presentingProblemItem && (
          <Textarea
            label="Presenting Problem"
            value={getAnswerString(getNestedResponseItem(diagnosisInstance, 'presenting-problem'))}
            onChange={(e) => {
              if (fieldDisabled) return;
              setStringAnswer(formState, diagnosisContext, presentingProblemItem, e.currentTarget.value);
            }}
            placeholder="Describe the presenting problem..."
            minRows={TREATMENT_PLAN_TEXTAREA_MIN_ROWS}
            autosize
            readOnly={fieldDisabled}
            styles={{ input: TREATMENT_PLAN_INPUT_BG }}
          />
        )}

        <Box>
          <Text size="sm" fw={600} mb="sm">Symptoms</Text>
          <Stack gap="md">
            {behavioralInstances.map((instance, index) => (
              <Box key={instance.id ?? `symptom-${index}`}>
                <Group justify="space-between" align="center" mb={4}>
                  <Text size="xs" c="dimmed" fw={600}>
                    {index === 0 ? 'Symptom 1' : `Symptom ${index + 1}`}
                  </Text>
                  {!fieldDisabled && behavioralInstances.length > 1 && (
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => removeInstance(diagnosisContext, instance)}
                      aria-label="Remove symptom"
                      size="sm"
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                </Group>
                <RepeatableTextField
                  value={getAnswerString(getNestedResponseItem(instance, '-behavioral-definition-text'))}
                  onChange={(e) => {
                    if (fieldDisabled) return;
                    setStringAnswer(
                      formState,
                      [...diagnosisContext, instance],
                      behavioralDefinitionTextItem,
                      e.currentTarget.value,
                    );
                  }}
                  placeholder="Enter a client-reported or provider-confirmed symptom…"
                  disabled={fieldDisabled}
                />
              </Box>
            ))}
            {!fieldDisabled && (
              <Button
                variant="default"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => formState.onAddGroup(diagnosisContext, behavioralDefinitionGroupItem)}
                style={{ alignSelf: 'flex-start' }}
              >
                Add symptom
              </Button>
            )}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Group justify="space-between" align="center" mb="md">
            <Title order={5}>Goals</Title>
            {!fieldDisabled && (
              <Button
                color="gold"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => formState.onAddGroup(diagnosisContext, goalGroupItem)}
              >
                Add goal
              </Button>
            )}
          </Group>
          <Stack gap="lg">
            {goalInstances.map((goalInstance, goalIndex) => {
              const objectiveInstances = filterResponseItemsByLinkId(
                goalInstance.item,
                objectiveGroupItem.linkId,
              );
              const goalContext = [...diagnosisContext, goalInstance];

              return (
                <Box key={goalInstance.id ?? `goal-${goalIndex}`}>
                  <Group justify="space-between" align="center" mb="sm">
                    <Text size="xs" c="dimmed" fw={600}>
                      {goalIndex === 0 ? 'First Goal' : `Goal ${goalIndex + 1}`}
                    </Text>
                    {!fieldDisabled && goalInstances.length > 1 && (
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => removeInstance(diagnosisContext, goalInstance)}
                        aria-label="Remove goal"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>

                  <RepeatableTextField
                    value={getAnswerString(getNestedResponseItem(goalInstance, '-goal-text'))}
                    onChange={(e) => {
                      if (fieldDisabled) return;
                      setStringAnswer(formState, goalContext, goalTextItem, e.currentTarget.value);
                    }}
                    placeholder="Goal description..."
                    disabled={fieldDisabled}
                  />

                  <Stack gap="md" mt="md">
                    {objectiveInstances.map((objectiveInstance, objIndex) => {
                      const objectiveContext = [...goalContext, objectiveInstance];
                      return (
                        <Box key={objectiveInstance.id ?? `obj-${objIndex}`}>
                          <Group justify="space-between" align="center" mb={4}>
                            <Text size="xs" c="dimmed" fw={600}>
                              Objective {objIndex + 1}
                            </Text>
                            {!fieldDisabled && (
                              <ActionIcon
                                variant="light"
                                color="red"
                                onClick={() => removeInstance(goalContext, objectiveInstance)}
                                aria-label="Remove objective"
                                size="sm"
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            )}
                          </Group>
                          <RepeatableTextField
                            value={getAnswerString(getNestedResponseItem(objectiveInstance, '-objective-text'))}
                            onChange={(e) => {
                              if (fieldDisabled || !objectiveTextItem) return;
                              setStringAnswer(
                                formState,
                                objectiveContext,
                                objectiveTextItem,
                                e.currentTarget.value,
                              );
                            }}
                            placeholder="Objective description..."
                            disabled={fieldDisabled}
                          />
                          {interventionsItem && (
                            <Box mt="sm">
                              <Text size="xs" c="gray.6" fw={500} mb="xs">Interventions</Text>
                              <Textarea
                                value={getAnswerString(getNestedResponseItem(objectiveInstance, '-interventions'))}
                                onChange={(e) => {
                                  if (fieldDisabled) return;
                                  setStringAnswer(
                                    formState,
                                    objectiveContext,
                                    interventionsItem,
                                    e.currentTarget.value,
                                  );
                                }}
                                placeholder="Enter interventions..."
                                minRows={TREATMENT_PLAN_TEXTAREA_MIN_ROWS}
                                autosize
                                readOnly={fieldDisabled}
                                styles={{ input: TREATMENT_PLAN_INPUT_BG }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    {!fieldDisabled && (
                      <Button
                        variant="default"
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => formState.onAddGroup(goalContext, objectiveGroupItem)}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        Add objective
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export function TreatmentPlanDocumentFormSection({
  questionnaire,
  savedResponse,
  readOnlyLinkIds,
  isEditable,
  varCtx,
  headerContent,
  onLiveResponseChange,
  onFormChange,
  pendingAiSuggestion,
  onAiSuggestionApplied,
  onAiSuggestionError,
}: TreatmentPlanDocumentFormSectionProps): JSX.Element {
  const formDefaultValue = useMemo(() => {
    if (!savedResponse) return undefined;
    return {
      ...savedResponse,
      item: shapeNestedResponseItemsForQuestionnaire(savedResponse.item, questionnaire) ?? savedResponse.item,
    };
  }, [questionnaire, savedResponse]);

  /** Bumped on real form mutations so live AI context refreshes even if formState mutates in place. */
  const [liveResponseEpoch, setLiveResponseEpoch] = useState(0);

  const formState = useQuestionnaireForm({
    questionnaire,
    defaultValue: formDefaultValue,
    disablePagination: true,
    onChange: () => {
      onFormChange?.();
      setLiveResponseEpoch((n) => n + 1);
    },
  });

  useEffect(() => {
    if ('loading' in formState && formState.loading) {
      onLiveResponseChange(savedResponse ?? undefined);
      return;
    }
    if ('questionnaireResponse' in formState) {
      const qr = formState.questionnaireResponse;
      // Shallow-copy so parent memos keyed on item identity see diagnosis/symptom edits.
      onLiveResponseChange(
        qr
          ? { ...qr, item: qr.item ? qr.item.map((entry) => ({ ...entry })) : qr.item }
          : undefined,
      );
    }
  }, [formState, onLiveResponseChange, savedResponse, liveResponseEpoch]);

  const loadedFormState = useMemo((): LoadedQuestionnaireFormState | undefined => (
    isLoadedQuestionnaireFormState(formState) ? formState : undefined
  ), [formState]);
  const formItems = loadedFormState?.items ?? [];
  const formResponseItems = loadedFormState?.responseItems ?? [];

  const planInfoItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(formItems, '-plan-info'),
    [formItems],
  );
  const dateAssignedItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(planInfoItem?.item, '-date-assigned'),
    [planInfoItem],
  );
  const expirationDateItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(planInfoItem?.item, '-expiration-date'),
    [planInfoItem],
  );
  const treatmentFrequencyItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(planInfoItem?.item, '-treatment-frequency'),
    [planInfoItem],
  );
  /**
   * Plan-info children minus the fields with dedicated renderers (frequency
   * Autocomplete + date grid). Rendered directly — not through the generic
   * question shell, whose answer box indents content — so everything stays
   * aligned with the page's left edge.
   */
  const planInfoChildrenWithoutDates = useMemo(
    (): QuestionnaireItem[] =>
      planInfoItem?.item?.filter(
        (child) =>
          child !== dateAssignedItem && child !== expirationDateItem && child !== treatmentFrequencyItem,
      ) ?? [],
    [dateAssignedItem, expirationDateItem, planInfoItem, treatmentFrequencyItem],
  );
  const diagnosisGroupItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(formItems, '-diagnosis'),
    [formItems],
  );
  const extensionItems = useMemo(
    () => formItems.filter((item) => !isCoreTreatmentPlanTopLevelItem(item)),
    [formItems],
  );

  const diagnosisCodeItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(diagnosisGroupItem?.item, '-diagnosis-code'),
    [diagnosisGroupItem],
  );
  const presentingProblemItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(diagnosisGroupItem?.item, 'presenting-problem'),
    [diagnosisGroupItem],
  );
  const behavioralDefinitionGroupItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(diagnosisGroupItem?.item, '-behavioral-definition'),
    [diagnosisGroupItem],
  );
  const behavioralDefinitionTextItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(behavioralDefinitionGroupItem?.item, '-behavioral-definition-text'),
    [behavioralDefinitionGroupItem],
  );
  const goalGroupItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(diagnosisGroupItem?.item, '-goal'),
    [diagnosisGroupItem],
  );
  const goalTextItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(goalGroupItem?.item, '-goal-text'),
    [goalGroupItem],
  );
  const objectiveGroupItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(goalGroupItem?.item, '-objective'),
    [goalGroupItem],
  );
  const objectiveTextItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(objectiveGroupItem?.item, '-objective-text'),
    [objectiveGroupItem],
  );
  const interventionsItem = useMemo(
    () => findQuestionnaireItemByLinkIdSuffix(objectiveGroupItem?.item, '-interventions'),
    [objectiveGroupItem],
  );

  // Do not memoize on formResponseItems: useQuestionnaireForm.onAddGroup mutates the
  // response item array in place, so the reference is stable across adds and a useMemo
  // keyed on that array would keep a stale diagnosis list.
  const diagnosisInstances = filterResponseItemsByLinkId(
    formResponseItems,
    diagnosisGroupItem?.linkId,
  );
  const planInfoInstance = filterResponseItemsByLinkId(formResponseItems, planInfoItem?.linkId)[0];
  const assignedAtValue = getPlanInfoDateAnswer(planInfoInstance, dateAssignedItem);
  const expiresAtValue = getPlanInfoDateAnswer(planInfoInstance, expirationDateItem);

  /**
   * Assigned date/time edits (interpreted as PT wall-clock time) always recompute
   * the read-only expiration to assigned + diagnosis-aware calendar days
   * (180 default; 90 for adjustment disorder).
   */
  const handleAssignedDateChange = (picked: Date | string | null): void => {
    if (!loadedFormState || !planInfoInstance || !dateAssignedItem) return;
    if (!picked) return;
    const iso = pacificWallClockToIso(new Date(String(picked)));
    loadedFormState.onChangeAnswer([planInfoInstance], dateAssignedItem, [{ valueDateTime: iso }]);
    if (expirationDateItem) {
      const days = resolveTreatmentPlanExpirationDays(formResponseItems);
      loadedFormState.onChangeAnswer([planInfoInstance], expirationDateItem, [
        { valueDateTime: addCalendarDays(iso, days) },
      ]);
    }
  };

  const fieldDisabled = !isEditable;
  const effectiveReadOnlyLinkIds = isEditable ? new Set<string>() : readOnlyLinkIds;

  useEffect(() => {
    if (!loadedFormState || !planInfoItem || fieldDisabled) return;
    const planInfoInstances = filterResponseItemsByLinkId(loadedFormState.responseItems, planInfoItem.linkId);
    if (planInfoInstances.length === 0) {
      loadedFormState.onAddGroup([], planInfoItem);
      return;
    }
    // Deterministic date defaults: assigned = now, expiration = assigned +
    // diagnosis-aware days (180 / 90 for adjustment disorder). Populates only
    // absent answers — provider-entered values are never overwritten.
    const parsed = parseTreatmentPlanDraft(loadedFormState.responseItems);
    if (parsed.assignedAt && parsed.expiresAt) return;
    const planInfoContext = [planInfoInstances[0]];
    const assignedAt = parsed.assignedAt ?? new Date().toISOString();
    if (!parsed.assignedAt && dateAssignedItem) {
      loadedFormState.onChangeAnswer(planInfoContext, dateAssignedItem, [{ valueDateTime: assignedAt }]);
    }
    if (!parsed.expiresAt && expirationDateItem) {
      const days = resolveTreatmentPlanExpirationDays(loadedFormState.responseItems);
      loadedFormState.onChangeAnswer(planInfoContext, expirationDateItem, [
        { valueDateTime: addCalendarDays(assignedAt, days) },
      ]);
    }
  }, [dateAssignedItem, expirationDateItem, fieldDisabled, loadedFormState, planInfoItem]);

  // When diagnoses change (e.g. add/remove adjustment disorder), keep expiration
  // aligned with the diagnosis-aware cycle while assigned stays fixed.
  const diagnosisCodesKey = diagnosisInstances
    .map((instance) => getAnswerString(getNestedResponseItem(instance, '-diagnosis-code')))
    .join('|');
  useEffect(() => {
    if (!loadedFormState || !planInfoInstance || !expirationDateItem || fieldDisabled) return;
    if (!assignedAtValue) return;
    const days = resolveTreatmentPlanExpirationDays(loadedFormState.responseItems);
    const nextExpires = addCalendarDays(assignedAtValue, days);
    if (expiresAtValue === nextExpires) return;
    loadedFormState.onChangeAnswer([planInfoInstance], expirationDateItem, [
      { valueDateTime: nextExpires },
    ]);
  }, [
    assignedAtValue,
    diagnosisCodesKey,
    expirationDateItem,
    expiresAtValue,
    fieldDisabled,
    loadedFormState,
    planInfoInstance,
  ]);

  useEffect(() => {
    if (!loadedFormState || !pendingAiSuggestion || fieldDisabled) return;
    try {
      applyTreatmentPlanRevisionOperations(
        loadedFormState,
        questionnaire,
        pendingAiSuggestion.operations,
        { replaceExistingContent: pendingAiSuggestion.replaceExistingContent === true },
      );
      onAiSuggestionApplied?.();
    } catch (error) {
      onAiSuggestionError?.(error instanceof Error ? error.message : 'Failed to apply AI suggestions');
      onAiSuggestionApplied?.();
    }
  }, [fieldDisabled, loadedFormState, onAiSuggestionApplied, onAiSuggestionError, pendingAiSuggestion, questionnaire]);

  const refreshForDiagnosisRemoval = useMemo((): {
    context: QuestionnaireResponseItem[];
    item: QuestionnaireItem;
  } | null => {
    if (planInfoItem) return { context: [], item: planInfoItem };
    if (diagnosisGroupItem) return { context: [], item: diagnosisGroupItem };
    return null;
  }, [diagnosisGroupItem, planInfoItem]);

  if (!loadedFormState) {
    return <Text size="sm" c="dimmed">Loading treatment plan…</Text>;
  }

  const canRenderDiagnosisBlocks =
    diagnosisGroupItem
    && diagnosisCodeItem
    && behavioralDefinitionGroupItem
    && behavioralDefinitionTextItem
    && goalGroupItem
    && goalTextItem
    && objectiveGroupItem
    && objectiveTextItem;

  return (
    <Stack gap="md">
      <Paper withBorder p="lg">
        <Stack gap="md">
          {headerContent}

          <Group gap="xs">
            <IconClipboardList size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
            <Title order={5}>Treatment Plan</Title>
          </Group>

          {planInfoInstance && treatmentFrequencyItem && (
            <Autocomplete
              label={treatmentFrequencyItem.text}
              required={treatmentFrequencyItem.required}
              data={TREATMENT_FREQUENCY_OPTIONS}
              value={getAnswerString(getNestedResponseItem(planInfoInstance, '-treatment-frequency'))}
              onChange={(value) => {
                if (fieldDisabled) return;
                setStringAnswer(loadedFormState, [planInfoInstance], treatmentFrequencyItem, value);
              }}
              placeholder="Select or type a frequency…"
              readOnly={fieldDisabled}
              styles={{ input: TREATMENT_PLAN_INPUT_BG }}
            />
          )}

          {planInfoInstance && planInfoChildrenWithoutDates.length > 0 && (
            <PreviewItemArray
              formState={loadedFormState}
              context={[planInfoInstance]}
              items={planInfoChildrenWithoutDates}
              responseItems={planInfoInstance.item ?? []}
              questionnaireItems={questionnaire.item}
              variableContext={varCtx}
              readOnlyLinkIds={effectiveReadOnlyLinkIds}
              readOnly={fieldDisabled}
            />
          )}

          {planInfoInstance && (dateAssignedItem || expirationDateItem) && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {dateAssignedItem && (
                fieldDisabled ? (
                  <TreatmentPlanDateText label={dateAssignedItem.text} iso={assignedAtValue} />
                ) : (
                  <DateTimePicker
                    label={dateAssignedItem.text}
                    required={dateAssignedItem.required}
                    placeholder="Pick date and time"
                    value={assignedAtValue ? isoToPacificWallClock(assignedAtValue) : null}
                    onChange={handleAssignedDateChange}
                    valueFormat="MM/DD/YYYY hh:mm A"
                    timePickerProps={{ format: '12h', withDropdown: true }}
                    leftSection={<IconCalendarEvent size={16} />}
                  />
                )
              )}
              {expirationDateItem && (
                <TreatmentPlanDateText label={expirationDateItem.text} iso={expiresAtValue} />
              )}
            </SimpleGrid>
          )}
        </Stack>
      </Paper>

      {canRenderDiagnosisBlocks && (
        <Stack gap="lg">
          {diagnosisInstances.map((diagnosisInstance, diagnosisIndex) => {
            const diagnosisContext: QuestionnaireResponseItem[] = [diagnosisInstance];
            const refreshItem = diagnosisCodeItem;
            const refreshContext = diagnosisContext;

            return (
              <DiagnosisBlock
                key={diagnosisInstance.id ?? `diagnosis-${diagnosisIndex}`}
                diagnosisInstance={diagnosisInstance}
                diagnosisIndex={diagnosisIndex}
                diagnosisContext={diagnosisContext}
                formState={loadedFormState}
                diagnosisCodeItem={diagnosisCodeItem}
                presentingProblemItem={presentingProblemItem}
                behavioralDefinitionGroupItem={behavioralDefinitionGroupItem}
                behavioralDefinitionTextItem={behavioralDefinitionTextItem}
                goalGroupItem={goalGroupItem}
                goalTextItem={goalTextItem}
                objectiveGroupItem={objectiveGroupItem}
                objectiveTextItem={objectiveTextItem}
                interventionsItem={interventionsItem}
                fieldDisabled={fieldDisabled}
                canRemoveDiagnosis={diagnosisInstances.length > 1}
                onAddDiagnosis={() => loadedFormState.onAddGroup([], diagnosisGroupItem)}
                onRemoveDiagnosis={() => {
                  if (!refreshForDiagnosisRemoval) return;
                  removeRepeatableGroupInstance(
                    loadedFormState,
                    [],
                    diagnosisInstance,
                    refreshForDiagnosisRemoval,
                  );
                }}
                refreshItem={refreshItem}
                refreshContext={refreshContext}
              />
            );
          })}
        </Stack>
      )}

      {extensionItems.length > 0 && (
        <>
          <Divider />
          <Text size="sm" fw={600}>Additional template fields</Text>
          <PreviewItemArray
            formState={loadedFormState}
            context={[]}
            items={extensionItems}
            responseItems={loadedFormState.responseItems}
            questionnaireItems={questionnaire.item}
            variableContext={varCtx}
            readOnlyLinkIds={effectiveReadOnlyLinkIds}
            readOnly={fieldDisabled}
          />
        </>
      )}
    </Stack>
  );
}
